/* --- Predictive Search Helper --- */
(function () {
  var themeStrings = (window.KPTheme && window.KPTheme.strings) || {
    searchLoading: 'Searching…',
    searchNoProducts: 'No products found',
    searchViewAll: 'View all results',
    searchFor: 'See all results for “___Q___”',
    searchProducts: 'Related Products',
    searchCollections: 'Collections',
    searchError: 'Search is temporarily unavailable. Press Enter to see the full search page.'
  };

  var cache = new Map();
  var routesRoot = window.Shopify?.routes?.root || '/';

  /* One outside-click owner for every predictive-search instance on the
     page (desktop input, mobile overlay input, /search page input),
     instead of a document listener per input. Each instance registers its
     container + close function here; the shared listener just walks the
     list. Detached forms are skipped via isConnected; the list is not
     pruned because production never reloads the header. */
  var registeredSearches = [];
  var outsideClickBound = false;

  function bindOutsideClickOnce() {
    if (outsideClickBound) return;
    outsideClickBound = true;
    document.addEventListener('click', (e) => {
      registeredSearches.forEach((entry) => {
        if (!entry.container.isConnected) return;
        if (!entry.container.contains(e.target)) entry.closeResults();
      });
    });
  }

  function setExpanded(input, resultsBox, expanded) {
    input.setAttribute('aria-expanded', String(expanded));
    resultsBox.classList.toggle('hidden', !expanded);
    const panel = resultsBox.closest('.mobile-search-overlay__panel');
    if (panel) panel.classList.toggle('is-open', expanded);
  }

  function renderMessage(resultsBox, message) {
    const element = document.createElement('p');
    element.className = 'predictive-search-message';
    element.textContent = message;
    resultsBox.replaceChildren(element);
  }

  function safeStorefrontUrl(value, fallback) {
    try {
      const url = new URL(value || fallback, window.location.origin);
      if (url.origin !== window.location.origin || !['http:', 'https:'].includes(url.protocol)) return fallback;
      return url.pathname + url.search + url.hash;
    } catch (error) {
      return fallback;
    }
  }

  function searchPageHref(query) {
    return routesRoot + 'search?q=' + encodeURIComponent(query) + '&type=product&options%5Bprefix%5D=last';
  }

  function viewAllLabel(query) {
    const tpl = themeStrings.searchFor || themeStrings.searchViewAll || 'See all results for “___Q___”';
    if (tpl.indexOf('___Q___') !== -1) return tpl.split('___Q___').join(query);
    return tpl.replace(/\{\{\s*terms\s*\}\}/g, query);
  }

  /* Shopify's /search/suggest.json returns `price` as a bare decimal string
     in MAJOR units for the active currency — "49.99", with no symbol and no
     locale formatting. Rendering it raw put "49.99" next to every search
     result: no £, no $, nothing. Format it here the same way the cart and
     drawer format their totals, and fall back to the raw string rather than
     showing nothing if anything about the value is unexpected. */
  function formatSuggestPrice(value) {
    if (value === null || value === undefined || value === '') return '';
    const amount = parseFloat(String(value).replace(/[^0-9.\-]/g, ''));
    if (!isFinite(amount)) return String(value);
    if (window.KPTheme && typeof KPTheme.formatMoney === 'function') {
      return KPTheme.formatMoney(Math.round(amount * 100));
    }
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: currency
      }).format(amount);
    } catch (error) {
      return amount.toFixed(2) + ' ' + currency;
    }
  }

  /* The suggest API hands back the original uploaded asset. These thumbnails
     render at 56px, so ask the CDN for a sized one instead of pulling a
     multi-megabyte original into a small square on every keystroke. 128 =
     64 at 2x density; keep it in step with the CSS box size. */
  function sizedThumb(src) {
    if (!src) return src;
    // Width only. height+crop=center squared the image at the CDN, which on
    // a tall studio shot returns the middle band — panel or bedding, no
    // product. The CSS pairs this with object-fit:contain, so it needs the
    // natural aspect ratio. Same reasoning as the sticky-bar thumbnail.
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=128';
  }

  function headingEl(text) {
    const heading = document.createElement('p');
    heading.className = 'predictive-search-heading';
    heading.textContent = text;
    return heading;
  }

  function collectionImageSrc(collection) {
    const image = collection && (collection.featured_image || collection.image);
    if (!image) return '';
    if (typeof image === 'string') return image;
    return image.url || image.src || '';
  }

  function appendCollectionRow(fragment, collection, resultsBox, query, index) {
    const link = document.createElement('a');
    const fallback = routesRoot + 'collections/' + encodeURIComponent(collection.handle || '');
    link.href = safeStorefrontUrl(collection.url, fallback);
    link.className = 'search-result-item search-result-item--collection';
    link.setAttribute('role', 'option');
    link.id = resultsBox.id + '-collection-' + index;

    const content = document.createElement('div');
    const title = document.createElement('p');
    title.className = 'search-result-title';
    title.textContent = collection.title || '';
    content.append(title);

    const src = collectionImageSrc(collection);
    if (src) {
      const image = document.createElement('img');
      image.alt = '';
      image.width = 56;
      image.height = 56;
      image.loading = 'eager';
      image.decoding = 'async';
      image.src = sizedThumb(src);
      link.append(image, content);
    } else {
      const ph = document.createElement('span');
      ph.className = 'search-result-item__placeholder';
      ph.setAttribute('aria-hidden', 'true');
      link.append(ph, content);
    }
    fragment.append(link);
  }

  function appendProductRow(fragment, product, resultsBox, query, index) {
    const link = document.createElement('a');
    link.href = safeStorefrontUrl(product.url, searchPageHref(query));
    link.className = 'search-result-item';
    link.setAttribute('role', 'option');
    link.id = resultsBox.id + '-option-' + index;

    const content = document.createElement('div');
    const title = document.createElement('p');
    title.className = 'search-result-title';
    title.textContent = product.title || '';
    content.append(title);

    if (product.type) {
      const type = document.createElement('p');
      type.className = 'search-result-type';
      type.textContent = product.type;
      content.append(type);
    }

    const price = document.createElement('p');
    price.className = 'search-result-price';
    price.textContent = formatSuggestPrice(product.price);
    content.append(price);

    if (product.image) {
      const image = document.createElement('img');
      image.alt = '';
      image.width = 56;
      image.height = 56;
      image.loading = 'eager';
      image.decoding = 'async';
      image.src = sizedThumb(product.image);
      link.append(image, content);
    } else {
      const ph = document.createElement('span');
      ph.className = 'search-result-item__placeholder';
      ph.setAttribute('aria-hidden', 'true');
      link.append(ph, content);
    }
    fragment.append(link);
  }

  function appendViewAll(fragment, resultsBox, query) {
    const viewAll = document.createElement('a');
    viewAll.href = searchPageHref(query);
    viewAll.className = 'predictive-search-view-all';
    viewAll.setAttribute('role', 'option');
    viewAll.id = (resultsBox.id || 'predictive') + '-view-all';
    viewAll.textContent = viewAllLabel(query);
    fragment.append(viewAll);
  }

  function soldCollectionHandles() {
    var handles = window.KPTheme && window.KPTheme.intlSoldHandles;
    return Array.isArray(handles) ? handles : null;
  }

  function filterSoldCollections(collections) {
    var sold = soldCollectionHandles();
    var list = collections || [];
    if (!sold) return list;
    return list.filter(function (collection) {
      return collection && sold.indexOf(collection.handle) !== -1;
    });
  }

  function normalizePayload(raw) {
    if (Array.isArray(raw)) return { products: raw, collections: [] };
    return {
      products: (raw && raw.products) || [],
      collections: filterSoldCollections((raw && raw.collections) || [])
    };
  }

  function renderResults(resultsBox, payload, query) {
    const data = normalizePayload(payload);
    const fragment = document.createDocumentFragment();

    if (data.collections.length) {
      fragment.append(headingEl(themeStrings.searchCollections || 'Collections'));
      data.collections.forEach((collection, index) => {
        appendCollectionRow(fragment, collection, resultsBox, query, index);
      });
    }

    if (data.products.length) {
      fragment.append(headingEl(themeStrings.searchProducts || 'Related Products'));
      data.products.forEach((product, index) => {
        appendProductRow(fragment, product, resultsBox, query, index);
      });
    }

    if (!data.products.length && !data.collections.length) {
      const empty = document.createElement('p');
      empty.className = 'predictive-search-message';
      empty.textContent = themeStrings.searchNoProducts;
      fragment.append(empty);
    }

    appendViewAll(fragment, resultsBox, query);
    resultsBox.replaceChildren(fragment);

    if (window.KPTheme && typeof window.KPTheme.track === 'function') {
      window.KPTheme.track('predictive_search', {
        query_length: query.length,
        result_count: data.products.length,
        collection_count: data.collections.length,
        source: 'network_or_cache'
      });
    }
  }

  function initSearchInput(input) {
    if (input.dataset.predictiveSearchInitialized === 'true') return;
    input.dataset.predictiveSearchInitialized = 'true';

    const container = input.closest('form');
    const resultsBox = container?.querySelector('.predictive-search-results');
    if (!resultsBox) return;

    let debounceTimer;
    let controller;
    let requestSequence = 0;

    function closeResults() {
      setExpanded(input, resultsBox, false);
      input.removeAttribute('aria-activedescendant');
    }

    registeredSearches.push({ container, closeResults });

    input.addEventListener('input', () => {
      clearTimeout(debounceTimer);
      if (controller) controller.abort();
      const query = input.value.trim();

      if (query.length < 2) {
        resultsBox.replaceChildren();
        closeResults();
        return;
      }

      renderMessage(resultsBox, themeStrings.searchLoading);
      setExpanded(input, resultsBox, true);

      debounceTimer = setTimeout(() => {
        const normalizedQuery = query.toLocaleLowerCase(document.documentElement.lang || undefined);
        const sequence = ++requestSequence;

        if (cache.has(normalizedQuery)) {
          renderResults(resultsBox, cache.get(normalizedQuery), query);
          setExpanded(input, resultsBox, true);
          return;
        }

        controller = new AbortController();
        const requestController = controller;
        let requestTimedOut = false;
        const requestTimer = window.setTimeout(() => {
          requestTimedOut = true;
          requestController.abort();
        }, 12000);
        const suggestUrl = routesRoot
          + 'search/suggest.json?q=' + encodeURIComponent(query)
          + '&resources[type]=product,collection'
          + '&resources[limit]=10'
          + '&resources[limit_scope]=each';

        fetch(suggestUrl, {
          signal: controller.signal,
          headers: { 'Accept': 'application/json' }
        })
          .then((response) => {
            if (!response.ok) throw new Error('suggest ' + response.status);
            return response.json();
          })
          .then(json => {
            if (sequence !== requestSequence || input.value.trim() !== query) return;
            const payload = {
              products: json?.resources?.results?.products || [],
              collections: json?.resources?.results?.collections || []
            };
            cache.set(normalizedQuery, payload);
            renderResults(resultsBox, payload, query);
            setExpanded(input, resultsBox, true);
          })
          .catch(error => {
            if (sequence !== requestSequence) return;
            if (error.name === 'AbortError' && !requestTimedOut) return;
            console.error('Suggest error', error);
            renderMessage(resultsBox, themeStrings.searchError);
            setExpanded(input, resultsBox, true);
          })
          .finally(() => window.clearTimeout(requestTimer));
      }, 300);
    });

    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeResults();
        return;
      }
      const options = Array.from(resultsBox.querySelectorAll('[role="option"]'));
      if (!options.length || !['ArrowDown', 'ArrowUp'].includes(event.key)) return;

      event.preventDefault();
      const activeIndex = options.indexOf(document.activeElement);
      const nextIndex = event.key === 'ArrowDown'
        ? (activeIndex + 1) % options.length
        : (activeIndex <= 0 ? options.length - 1 : activeIndex - 1);
      options[nextIndex].focus();
      input.setAttribute('aria-activedescendant', options[nextIndex].id);
    });

    resultsBox.addEventListener('keydown', (event) => {
      const options = Array.from(resultsBox.querySelectorAll('[role="option"]'));
      const activeIndex = options.indexOf(document.activeElement);
      if (event.key === 'Escape') {
        event.preventDefault();
        closeResults();
        input.focus();
      } else if (event.key === 'ArrowDown' && options.length) {
        event.preventDefault();
        options[(activeIndex + 1) % options.length].focus();
      } else if (event.key === 'ArrowUp' && options.length) {
        event.preventDefault();
        if (activeIndex <= 0) input.focus();
        else options[activeIndex - 1].focus();
      }
    });
  }

  function initPredictiveSearch(root) {
    const scope = root || document;
    bindOutsideClickOnce();
    scope.querySelectorAll('input[type="search"]').forEach(initSearchInput);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initPredictiveSearch(document));
  } else {
    initPredictiveSearch(document);
  }

  /* Header/search-section reloads in the theme editor replace the DOM the
     inputs above were bound to. Re-scan, scoped to the reloaded section, so
     a fresh input gets wired up — the per-input dataset guard means an
     unchanged input already in the DOM is never touched twice. */
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector('input[type="search"]')) {
      initPredictiveSearch(event.target);
    }
  });
})();
